import React, { useRef, useState } from 'react'
import { Animated, FlatList, StyleSheet } from 'react-native'

import { MotiView } from '@zet/common/components/motiView'
import {
  B5,
  colors,
  Icon,
  Pressable,
  SH3,
  Stack,
  XStack,
} from '@zet/design-system'
import { CategoryHeader, FaqsProps } from '@zet/zet-prime'
import { AnimatePresence } from 'moti'

export default function Faqs({ faqs, title }: FaqsProps) {
  const [expandedIndex, setExpandedIndex] = useState<Array<number>>([])
  const rotationValues = useRef<{ [key: number]: Animated.Value }>(
    faqs.reduce(
      (acc, _, index) => {
        acc[index] = new Animated.Value(0)
        return acc
      },
      {} as { [key: number]: Animated.Value },
    ),
  ).current

  const handlePress = (index: number) => {
    const isExpanded = expandedIndex.includes(index)

    Animated.timing(rotationValues[index], {
      toValue: isExpanded ? 0 : 1,
      duration: 300,
      useNativeDriver: true,
    }).start()

    setExpandedIndex(
      isExpanded
        ? expandedIndex.filter((i) => i !== index)
        : [...expandedIndex, index],
    )
  }

  const renderItem = ({
    item,
    index,
  }: {
    item: { question: string; answer: string }
    index: number
  }) => {
    const rotation = rotationValues[index].interpolate({
      inputRange: [0, 1],
      outputRange: ['0deg', '180deg'],
    })

    const isExpanded = expandedIndex.includes(index)

    return (
      <Stack className="rounded-lg border border-grays-30 p-3 bg-white">
        <Pressable onPress={() => handlePress(index)} className="w-full">
          <XStack className="items-center justify-between w-full">
            <SH3 className="flex-1 mr-2">{item.question}</SH3>
            <Animated.View style={{ transform: [{ rotate: rotation }] }}>
              <Icon type="ArrowDown" />
            </Animated.View>
          </XStack>
        </Pressable>
        <AnimatePresence>
          {isExpanded && (
            <MotiView
              from={{ maxHeight: 0 }}
              animate={{ maxHeight: 1000 }}
              exit={{ maxHeight: 0 }}
              transition={{ type: 'timing', duration: 300 }}
            >
              <Stack style={styles.answer}>
                <B5 className={`text-[${colors.grays[70]}] font-normal`}>
                  {item.answer}
                </B5>
              </Stack>
            </MotiView>
          )}
        </AnimatePresence>
      </Stack>
    )
  }

  return (
    <Stack className="px-4 py-3">
      <CategoryHeader title={title} />
      <FlatList
        data={faqs}
        renderItem={renderItem}
        ItemSeparatorComponent={() => <Stack className="h-[6]" />}
        contentContainerStyle={styles.list}
      />
    </Stack>
  )
}

const styles = StyleSheet.create({
  answer: {
    overflow: 'hidden',
    backgroundColor: colors.grays[20],
    padding: 12,
    borderRadius: 2,
    marginTop: 8,
  },
  list: {
    marginTop: 12,
  },
})
